import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
    Avatar,
    Badge,
    Dropdown,
    ScrollBar,
    Spinner,
} from 'components/ui'
import { HiOutlineBell } from 'react-icons/hi'
import withHeaderItem from 'utils/hoc/withHeaderItem'
import useResponsive from 'hooks/useResponsive'
import {
    getNotificationUnreadCount,
    getNotifications,
    markNotificationAsRead,
} from 'services/NotificationService'

/**
 * NotificationBell.
 *
 * Implements: Requirements 17.3, 17.4, 17.5
 *
 * Renders a bell icon in the application header with an unread-count
 * badge. Opening the dropdown fetches the most recent notifications
 * (capped to NOTIFICATION_PANEL_LIMIT == 5 per the design) and offers
 * a "View all" link to the full notification list page. Clicking a
 * notification marks it as read in the backend (PATCH
 * /notifications/:id/read) and updates the local list and badge so the
 * user gets immediate feedback even if the next poll has not run yet.
 *
 * The component intentionally uses the same `withHeaderItem` HOC and
 * `Dropdown` primitives as `UserDropdown` so it visually matches the
 * existing header items in every layout (Classic, Simple, Modern,
 * Stacked, Decked).
 */

const NOTIFICATION_PANEL_LIMIT = 5
const NOTIFICATION_PANEL_HEIGHT = 'h-72'
const VIEW_ALL_PATH = '/notifications'

const resolveNotificationPath = (item) => {
    if (item?.resource_type === 'kpi_assignment' && item?.resource_id) {
        return `/kpi/assignments/${item.resource_id}`
    }
    return null
}

const formatNotificationDate = (value) => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleString()
}

const NotificationToggle = React.forwardRef(({ className, count }, ref) => {
    return (
        <div ref={ref} className={classNames('text-2xl', className)}>
            {count > 0 ? (
                <Badge badgeStyle={{ top: '3px', right: '6px' }}>
                    <HiOutlineBell />
                </Badge>
            ) : (
                <HiOutlineBell />
            )}
        </div>
    )
})

const NotificationItem = ({ item, onClick }) => {
    const isUnread = !item.is_read
    return (
        <button
            type="button"
            onClick={() => onClick(item)}
            className={classNames(
                'w-full text-left flex px-4 py-3 cursor-pointer',
                'hover:bg-gray-50 active:bg-gray-100',
                'dark:hover:bg-black dark:hover:bg-opacity-20',
                isUnread ? 'bg-blue-50 dark:bg-blue-500/10' : ''
            )}
        >
            <div className="flex-shrink-0">
                <Avatar
                    shape="circle"
                    className={classNames(
                        'bg-blue-100 text-blue-600',
                        'dark:bg-blue-500/20 dark:text-blue-100'
                    )}
                    icon={<HiOutlineBell />}
                />
            </div>
            <div className="ltr:ml-3 rtl:mr-3 flex-1 min-w-0">
                <div className="font-semibold heading-text truncate">
                    {item.title}
                </div>
                {item.body && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                        {item.body}
                    </div>
                )}
                <div className="text-xs mt-1 text-gray-500">
                    {formatNotificationDate(item.created_date)}
                </div>
            </div>
            {isUnread && (
                <Badge
                    className="absolute top-4 ltr:right-4 rtl:left-4"
                    innerClass="bg-blue-500"
                />
            )}
        </button>
    )
}

export const NotificationBell = ({ className }) => {
    const navigate = useNavigate()
    const [items, setItems] = useState([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(false)

    const { larger } = useResponsive()
    const direction = useSelector((state) => state.theme?.direction)

    const refreshUnreadCount = useCallback(async () => {
        try {
            const resp = await getNotificationUnreadCount()
            const count = resp?.unreadCount ?? 0
            setUnreadCount(count)
        } catch (e) {
            // Network / auth errors are handled by the shared apiClient
            // (toast + signout on 401). We swallow here so the bell does
            // not crash the layout when notifications are temporarily
            // unavailable.
        }
    }, [])

    useEffect(() => {
        refreshUnreadCount()
    }, [refreshUnreadCount])

    const onDropdownOpen = useCallback(async () => {
        if (hasLoaded) return
        setLoading(true)
        try {
            const resp = await getNotifications({
                page: 1,
                limit: NOTIFICATION_PANEL_LIMIT,
            })
            setItems(Array.isArray(resp?.items) ? resp.items : [])
            if (typeof resp?.unreadCount === 'number') {
                setUnreadCount(resp.unreadCount)
            }
            setHasLoaded(true)
        } catch (e) {
            // Errors surfaced by apiClient; leave panel empty.
        } finally {
            setLoading(false)
        }
    }, [hasLoaded])

    const onItemClick = useCallback(
        async (item) => {
            const targetPath = resolveNotificationPath(item)
            if (!item.is_read) {
                // Optimistic update — flip locally first so the user gets
                // immediate feedback. If the backend call fails the apiClient
                // toast surfaces the error and we re-sync with the server.
                setItems((prev) =>
                    prev.map((row) =>
                        row.id === item.id ? { ...row, is_read: true } : row
                    )
                )
                setUnreadCount((prev) => Math.max(0, prev - 1))
                try {
                    await markNotificationAsRead(item.id)
                } catch (e) {
                    // Rollback on failure and re-fetch from source of truth.
                    refreshUnreadCount()
                    return
                }
            }
            if (targetPath) {
                navigate(targetPath)
            }
        },
        [navigate, refreshUnreadCount]
    )

    return (
        <Dropdown
            renderTitle={
                <NotificationToggle
                    count={unreadCount}
                    className={className}
                />
            }
            menuClass="p-0 min-w-[280px] md:min-w-[340px]"
            placement={larger.md ? 'bottom-end' : 'bottom-center'}
            onOpen={onDropdownOpen}
        >
            <Dropdown.Item variant="header">
                <div className="border-b border-gray-200 dark:border-gray-600 px-4 py-2 flex items-center justify-between">
                    <h6>Notifications</h6>
                    {unreadCount > 0 && (
                        <span className="text-xs text-gray-500">
                            {unreadCount} unread
                        </span>
                    )}
                </div>
            </Dropdown.Item>
            <div
                className={classNames(
                    'overflow-y-auto',
                    NOTIFICATION_PANEL_HEIGHT
                )}
            >
                <ScrollBar direction={direction}>
                    {!loading && items.length > 0 &&
                        items.map((item) => (
                            <div
                                key={item.id}
                                className="relative border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                            >
                                <NotificationItem
                                    item={item}
                                    onClick={onItemClick}
                                />
                            </div>
                        ))}
                    {loading && (
                        <div
                            className={classNames(
                                'flex items-center justify-center',
                                NOTIFICATION_PANEL_HEIGHT
                            )}
                        >
                            <Spinner size={40} />
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div
                            className={classNames(
                                'flex items-center justify-center',
                                NOTIFICATION_PANEL_HEIGHT
                            )}
                        >
                            <div className="text-center">
                                <h6 className="font-semibold">
                                    No notifications
                                </h6>
                                <p className="mt-1 text-sm text-gray-500">
                                    You are all caught up.
                                </p>
                            </div>
                        </div>
                    )}
                </ScrollBar>
            </div>
            <Dropdown.Item variant="header">
                <div className="flex justify-center border-t border-gray-200 dark:border-gray-600 px-4 py-2">
                    <Link
                        to={VIEW_ALL_PATH}
                        className="font-semibold cursor-pointer p-2 px-3 text-gray-600 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white"
                    >
                        View all
                    </Link>
                </div>
            </Dropdown.Item>
        </Dropdown>
    )
}

export const NOTIFICATION_BELL_PANEL_LIMIT = NOTIFICATION_PANEL_LIMIT

export default withHeaderItem(NotificationBell)
